import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EmiHttpGetOptions, EmiHttpBaseOptions, EmiHttpHeaders, EmiHttpParams } from '@core/models/http.models';
import { QueryFilters, QueryParams } from '@core/models/query-params.models';
import { EmiHttpUtils } from '@core/utils/http.utils';
import { Observable } from 'rxjs';

type HttpOptions<R> = {
  responseType?: R;
  headers?: HttpHeaders;
  params?: HttpParams;
};

@Injectable({ providedIn: 'root' })
export class HttpApi {
  constructor(private readonly http: HttpClient) {}

  get<T>(url: string, options?: EmiHttpGetOptions): Observable<T> {
    return this.http.get<T>(url, this.handleOptions(options));
  }

  post<T>(url: string, body: unknown | null, options?: EmiHttpGetOptions): Observable<T> {
    return this.http.post<T>(url, body, this.handleOptions(options));
  }

  patch<T>(url: string, body: unknown | null, options?: EmiHttpGetOptions): Observable<T> {
    return this.http.patch<T>(url, body, this.handleOptions(options));
  }

  delete<T>(url: string, options?: EmiHttpGetOptions): Observable<T> {
    return this.http.delete<T>(url, this.handleOptions(options));
  }

  private handleOptions<R>(options: EmiHttpBaseOptions | undefined): HttpOptions<R> {
    if (!options) return {};

    const params: HttpParams = this.handleParams((options.params as EmiHttpParams<QueryParams>) || {});

    let headers: HttpHeaders = this.transformHeaders(options.headers as EmiHttpHeaders);

    if (options.skipSuccessMessage) {
      headers = EmiHttpUtils.skipSuccessMessage(headers);
    }

    return { ...options, params, headers } as HttpOptions<R>;
  }

  private handleParams(params: EmiHttpParams<QueryParams>): HttpParams {
    let httpParams: HttpParams = new HttpParams();

    Object.keys(params).forEach((key: string) => {
      if (key === 'filters') {
        httpParams = EmiHttpUtils.filtersToHttpParams(httpParams, params.filters as QueryFilters);
      } else {
        httpParams = httpParams.append(key, params[key as keyof QueryParams] as string | number | boolean);
      }
    });

    return httpParams;
  }

  private transformHeaders(headers: EmiHttpHeaders): HttpHeaders {
    let _headers: HttpHeaders = new HttpHeaders();

    if (headers instanceof Object) {
      for (const key in headers) {
        _headers = _headers.set(key, headers[key]);
      }
    }

    return _headers;
  }
}
